import { linkOptions } from '@tanstack/react-router';

import type { WorkflowView } from '@/lib/api/generated/model';

/**
 * Where a workflow opens (spec 094).
 *
 * One rule, in one place, because four surfaces need it: the navigation's workflow
 * list, the workflow list page, the command palette, and the redirect that keeps
 * `/workflows/{key}` alive for anyone holding that address. Four copies of
 * `status === 'published' ? … : …` would be four chances to disagree, and the
 * disagreement would be invisible until somebody clicked the fourth one
 * (constitution §2.3; spec 094 FR-009).
 *
 * **Published opens its board; a draft opens its builder.** Not a preference — a
 * draft has no board. It has no committed definition, so its board would have no
 * columns and nothing could be placed on them. Sending a draft to a board would
 * spend a click to arrive at an explanation, on the one row that most needs an
 * action.
 *
 * **Total by construction.** A status this build does not recognise still resolves,
 * to the builder: a row that does nothing when clicked is worse than one that goes
 * somewhere imperfect, and an unknown status is far likelier to be a workflow that
 * is not ready than one that is (contracts/navigation.md, obligation 7).
 */
export function workflowDestination(
  workflow: Pick<WorkflowView, 'key' | 'status'>,
  workspaceId: string,
) {
  const params = { workspaceId, workflowKey: workflow.key };

  return workflow.status === 'published'
    ? linkOptions({
        to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
        params,
      })
    : linkOptions({
        to: '/workspaces/$workspaceId/workflows/$workflowKey/builder',
        params,
      });
}

/**
 * Whether a workflow's destination is its board.
 *
 * Exported so a caller can *describe* a row — "opens its board" — without
 * re-deriving the rule from `status` and thereby creating the second opinion this
 * module exists to prevent.
 */
export function opensOnBoard(workflow: Pick<WorkflowView, 'status'>): boolean {
  return workflow.status === 'published';
}
